// components/ProductCard.tsx
'use client';

import Image from 'next/image';
import Link from 'next/link';
import {
	ShoppingCart,
	Star,
	Package,
	Store,
	CheckCircle2,
} from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { useState } from 'react';

type ProductProps = {
	product: {
		_id: string;
		name: string;
		price: number;
		images?: { url?: string }[] | string[];
		storeName?: string;
		store?: any; // Handle populated store
		slug?: string;
		category?: any;
		stock?: number;
	};
	showStoreName?: boolean;
};

export default function ProductCard({
	product,
	showStoreName = true,
}: ProductProps) {
	const { addItem } = useCart();
	const [isAdded, setIsAdded] = useState(false);

	console.log(product, 'product in card');

	// Helper: Image URL extraction
	const imageUrl = Array.isArray(product.images)
		? typeof product.images[0] === 'string'
			? product.images[0]
			: (product.images[0] as any)?.url
		: typeof product.images === 'string'
		? product.images
		: undefined;

	// Helper: Currency Format
	const formatCurrency = (n: number) => {
		return new Intl.NumberFormat('en-NG', {
			style: 'currency',
			currency: 'NGN',
			maximumFractionDigits: 0,
		}).format(n);
	};

	const handleAddToCart = (e: React.MouseEvent) => {
		e.preventDefault(); // Prevent navigation
		e.stopPropagation();

		addItem(
			{
				id: product._id,
				name: product.name,
				price: product.price,
				image: imageUrl || '',
				category: product.category.name,
				storeId: product.store?._id,
				storeName: product.storeName || product.store?.name,
			},
			1,
		);

		setIsAdded(true);
		setTimeout(() => setIsAdded(false), 1500);
	};

	const productHref = `/products/${
		product.slug || product._id
	}`;
	const storeName =
		product.storeName || product.store?.name;

	return (
		<Link
			href={productHref}
			className="group block bg-white rounded-2xl border border-gray-100 hover:border-gray-300 hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col h-full relative"
			aria-label={`View ${product.name}`}
		>
			{/* Image Container */}
			<div className="relative aspect-[4/4] bg-gray-100 overflow-hidden">
				{imageUrl ? (
					<Image
						src={imageUrl}
						alt={product.name}
						fill
						className="object-cover group-hover:scale-105 transition-transform duration-500"
						sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
					/>
				) : (
					<div className="w-full h-full flex items-center justify-center text-gray-300">
						<Package className="w-12 h-12 opacity-50" />
					</div>
				)}

				{/* Stock Badge */}
				{product.stock === 0 && (
					<div className="absolute top-2 right-2 bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wide z-10 shadow-sm">
						Out of Stock
					</div>
				)}
			</div>

			{/* Content */}
			<div className="p-4 flex flex-col flex-1">
				{/* Store Name & Category */}
				<div className="flex items-center justify-between mb-1.5">
					{showStoreName && storeName && (
						<div className="flex items-center text-xs text-gray-500 truncate max-w-[70%]">
							<Store className="w-3 h-3 mr-1 flex-shrink-0" />
							<span className="truncate">{storeName}</span>
							{product.store?.owner?.isAlumni && (
								<CheckCircle2 className="w-3 h-3 text-blue-500 ml-1 flex-shrink-0" fill="#EBF8FF" />
							)}
						</div>
					)}
					{/* {product.category.name && (
						<span className="text-[10px] font-medium bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full uppercase tracking-wider ml-auto">
							{product.category.name}
						</span>
					)} */}
				</div>

				{/* Title */}
				<h3
					className="font-semibold text-gray-900 mb-1 line-clamp-2 text-sm leading-snug group-hover:text-[#051449] transition-colors"
					title={product.name}
				>
					{product.name}
				</h3>

				{/* Price & Action */}
				<div className="mt-auto pt-3 flex items-center justify-between border-t border-gray-50">
					<div className="flex flex-col">
						<span className="text-xs text-gray-400 font-medium">
							Price
						</span>
						<span className="text-lg font-bold text-[#051449]">
							{formatCurrency(product.price)}
						</span>
					</div>

					<button
						onClick={handleAddToCart}
						disabled={product.stock === 0}
						className={`p-2.5 rounded-xl transition-all shadow-sm flex items-center justify-center ${
							isAdded
								? 'bg-green-500 text-white'
								: 'bg-[#FFCF23] text-[#051449] hover:bg-[#ffbf00] hover:scale-105 active:scale-95'
						} disabled:opacity-50 disabled:cursor-not-allowed`}
						title="Add to Cart"
					>
						{isAdded ? (
							<Star className="w-5 h-5 fill-current" />
						) : (
							<ShoppingCart className="w-5 h-5" />
						)}
					</button>
				</div>
			</div>
		</Link>
		// <div></div>
	);
}
